import { motion } from 'framer-motion';
import {
  ArrowRight,
  Book,
  Code2,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Github,
  Layers,
  Play,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const Documentation = () => {
  const [activeExample, setActiveExample] = useState('issue');

  const quickStartGuides = [
    {
      icon: Zap,
      title: 'Quick Start',
      description: 'Get up and running with Documint in under 5 minutes',
      time: '5 min',
      link: '/docs/quickstart',
    },
    {
      icon: Code2,
      title: 'API Reference',
      description: 'Complete API documentation with examples',
      time: '10 min',
      link: '/docs/api',
    },
    {
      icon: Play,
      title: 'Interactive Playground',
      description: 'Test API calls in our interactive environment',
      time: '15 min',
      link: '/docs/playground',
    },
    {
      icon: Github,
      title: 'Sample Applications',
      description: 'Full-featured examples in multiple languages',
      time: '30 min',
      link: '/docs/examples',
    },
  ];

  const apiEndpoints = [
    {
      method: 'POST',
      endpoint: '/v1/credentials/issue',
      description: 'Issue a new verifiable credential',
      category: 'Credentials',
    },
    {
      method: 'POST',
      endpoint: '/v1/credentials/verify',
      description: 'Verify a credential presentation',
      category: 'Verification',
    },
    {
      method: 'POST',
      endpoint: '/v1/dids/create',
      description: 'Create a new decentralized identifier',
      category: 'DIDs',
    },
    {
      method: 'GET',
      endpoint: '/v1/dids/{did}/document',
      description: 'Resolve a DID to its document',
      category: 'DIDs',
    },
  ];

  const codeExamples = {
    issue: {
      title: 'Issue a Credential',
      language: 'JavaScript',
      code: `const credential = await documint.credentials.issue({
  type: 'EmploymentCredential',
  subject: {
    id: 'did:example:holder123',
    position: 'Senior Engineer',
    company: 'TechCorp Inc.'
  },
  issuer: 'did:example:issuer456'
});

console.log('Credential issued:', credential.id);`,
    },
    verify: {
      title: 'Verify a Presentation',
      language: 'JavaScript',
      code: `const result = await documint.presentations.verify({
  presentation: presentationData,
  policies: [
    { type: 'age_over', value: 18 },
    { type: 'employment_verified' }
  ]
});

if (result.verified) {
  console.log('Presentation valid');
}`,
    },
    did: {
      title: 'Create a DID',
      language: 'JavaScript',
      code: `const did = await documint.dids.create({
  method: 'key',
  keyType: 'Ed25519'
});

console.log('DID created:', did.id);
console.log('Private key:', did.privateKey);`,
    },
  };

  const sdks = [
    {
      name: 'JavaScript/Node.js',
      description: 'Official SDK for JavaScript and Node.js applications',
      language: 'JavaScript',
      install: 'npm install @documint/sdk',
      github: 'https://github.com/documint/js-sdk',
    },
    {
      name: 'Python',
      description: 'Official SDK for Python applications',
      language: 'Python',
      install: 'pip install documint-python',
      github: 'https://github.com/documint/python-sdk',
    },
    {
      name: 'Go',
      description: 'Official SDK for Go applications',
      language: 'Go',
      install: 'go get github.com/documint/go-sdk',
      github: 'https://github.com/documint/go-sdk',
    },
    {
      name: 'React Native',
      description: 'Mobile SDK for React Native applications',
      language: 'JavaScript',
      install: 'npm install @documint/react-native',
      github: 'https://github.com/documint/react-native-sdk',
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-surface-100 py-20">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Developer
              <span className="text-neon-300"> Documentation</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Everything you need to integrate Self-Sovereign Identity into your
              applications. From quick starts to advanced implementation guides.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/docs/quickstart"
                className="btn-primary inline-flex items-center"
              >
                Get Started
                <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link
                to="/docs/api"
                className="btn-secondary inline-flex items-center"
              >
                <Book size={20} className="mr-2" />
                API Reference
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Start Guides */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose your path to implementing verifiable credentials and
              decentralized identity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {quickStartGuides.map((guide, index) => (
              <motion.div
                key={guide.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                  <guide.icon className="w-6 h-6 text-primary-600" />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {guide.title}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded">
                    {guide.time}
                  </span>
                </div>
                <p className="text-gray-600 mb-4">{guide.description}</p>
                <div className="flex items-center text-primary-600 group-hover:text-primary-700">
                  <span className="text-sm font-medium mr-2">Learn more</span>
                  <ArrowRight size={16} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="py-20 bg-surface-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Code Examples
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              See how easy it is to implement core SSI functionality with our
              APIs.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Example Selector */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-4">
                {Object.entries(codeExamples).map(([key, example]) => (
                  <button
                    key={key}
                    onClick={() => setActiveExample(key)}
                    className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
                      activeExample === key
                        ? 'bg-primary-600/30 border border-neon-300'
                        : 'bg-surface-200/20 border border-white/10 hover:bg-surface-200/40'
                    }`}
                  >
                    <h3
                      className={`font-semibold ${
                        activeExample === key ? 'text-white' : 'text-gray-300'
                      }`}
                    >
                      {example.title}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                      {example.language} example
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Code Display */}
            <motion.div
              key={activeExample}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="glass rounded-xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center space-x-3">
                  <Code2 className="w-5 h-5 text-neon-300" />
                  <span className="text-white font-medium">
                    {codeExamples[activeExample].title}
                  </span>
                </div>
                <button className="text-gray-400 hover:text-white transition-colors">
                  <Copy size={16} />
                </button>
              </div>
              <div className="p-4">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{codeExamples[activeExample].code}</code>
                </pre>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* API Reference */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              API Reference
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive documentation for all available endpoints and
              operations.
            </p>
          </motion.div>

          <div className="bg-gray-50 rounded-xl p-8">
            <div className="grid gap-4">
              {apiEndpoints.map((endpoint, index) => (
                <motion.div
                  key={`${endpoint.method}-${endpoint.endpoint}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span
                        className={`px-3 py-1 rounded text-sm font-medium ${
                          endpoint.method === 'GET'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {endpoint.method}
                      </span>
                      <code className="text-gray-800 font-mono">
                        {endpoint.endpoint}
                      </code>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {endpoint.category}
                      </span>
                      <ExternalLink size={16} className="text-gray-400" />
                    </div>
                  </div>
                  <p className="text-gray-600 mt-2">{endpoint.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SDKs */}
      <section className="py-20 bg-surface-100">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Official SDKs
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Use our official SDKs to integrate Documint into your favorite
              programming language.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {sdks.map((sdk, index) => (
              <motion.div
                key={sdk.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass p-6 rounded-xl"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {sdk.name}
                    </h3>
                    <p className="text-gray-300">{sdk.description}</p>
                  </div>
                  <a
                    href={sdk.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <Github size={20} />
                  </a>
                </div>

                <div className="bg-surface-200/20 rounded-lg p-3 mb-4">
                  <code className="text-neon-300 text-sm">{sdk.install}</code>
                </div>

                <div className="flex space-x-3">
                  <button className="btn-secondary flex-1 flex items-center justify-center">
                    <Download size={16} className="mr-2" />
                    Install
                  </button>
                  <button className="btn-primary flex-1 flex items-center justify-center">
                    <FileText size={16} className="mr-2" />
                    Docs
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Join the Developer Community
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Connect with other developers building on Self-Sovereign Identity.
              Get help, share knowledge, and shape the future of digital
              identity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn-primary inline-flex items-center">
                <Github size={20} className="mr-2" />
                GitHub Discussions
              </button>
              <button className="btn-secondary inline-flex items-center">
                <Layers size={20} className="mr-2" />
                Discord Community
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Documentation;
